/*
Welcome screen — single-step parité Android v1.4.0
(`ui/onboarding/WelcomeScreen.kt:45-144`).

Un seul écran scrollable : logo circulaire vert + titre + tagline +
caption + ligne confidentialité + CTA pleine largeur. Pas de carousel,
pas de swipe, pas de dots — Android n'a jamais eu de carousel ici, la
version 3-cards de la PWA était un ajout d'origine maintenant retiré
pour parité stricte.

L'animation pédagogique silhouette grise↔verte (`WelcomeAnimation` côté
Android : « non capturé » ↔ « capturé », respiration 4 s/leg + scale
0.85→1) est portée en CSS (`.welcome-animation`). Pas de gating
prefers-reduced-motion — frame-clock côté Android, doit jouer même à
échelle d'animation système 0.

Deux modes, gated par `inRoute.replay` :
- replay=false (1er lancement) : le CTA « Commencer » prompte la géoloc,
  marque `onboardingDone`, et navigue vers la carte.
- replay=true (entrée depuis Profile) : CTA « Fermer », pas de prompt,
  pas de write — pop history ou fallback profile.
*/

import { navigate, kPaths, type Route } from '../router'
import { markOnboardingDone } from '../data/onboardingStore'
import { bootstrapGeolocation } from '../capture/geolocation'
import { renderIconPlataneSvg } from './components/IconPlatane'

export function mountWelcomeScreen(inRoot: HTMLElement, inRoute: Route): void
{
	const vReplay = inRoute.id === 'welcome' && inRoute.replay === true
	const vCtaLabel = vReplay ? 'Fermer' : 'Commencer'

	inRoot.innerHTML = `
		<div class="screen welcome welcome-single">
			<div class="welcome-scroll">
				<div class="welcome-hero-logo" aria-hidden="true">${renderHeroLogoSvg()}</div>

				<h1 class="display welcome-title">CanoPaname</h1>

				<p class="welcome-tagline">Chasse les arbres remarquables de Paris</p>

				<div class="welcome-animation" aria-hidden="true">${renderIconPlataneSvg('welcome-animation-tree')}</div>

				<p class="welcome-caption">
					Approche-toi d'un arbre, capture-le, et regarde son espèce
					passer au vert sur toute la carte.
				</p>

				<p class="welcome-privacy">
					Tout reste sur ton téléphone&nbsp;: pas de cloud, pas de compte.
				</p>

				<button class="welcome-button welcome-button-primary" id="welcome-cta" type="button">
					${vCtaLabel}
				</button>
			</div>
		</div>
	`

	const vCta = inRoot.querySelector<HTMLButtonElement>('#welcome-cta')
	if (!vCta) return

	vCta.addEventListener('click', () => { void onFinish(vReplay) })
}

/*
Hero logo : cercle vert FeuilleSombre #2E7D32 (parité
`MaterialTheme.arbresColors.feuilleSombre`) + silhouette canopée
crème centrée, équivalent du `Icon(ic_arbre_canonical, tint =
remarquableOrange)` Android mais en silhouette héritée — l'icône arbre
canonique Android est traitée séparément par Bug 7 (audit platane
PWA-only).
*/
function renderHeroLogoSvg(): string
{
	return `
		<svg viewBox="0 0 96 96" xmlns="http://www.w3.org/2000/svg" role="img">
			<circle cx="48" cy="48" r="48" fill="#2E7D32"/>
			<path d="M42.7,80 L42.7,58.7 C33.8,58.7 26.7,55.1 23.1,48 C19.5,46.2 19.5,39.1 21.3,33.8 C19.5,26.7 24.9,19.6 32,19.6 C33.8,14.2 40.9,10.7 48,10.7 C55.1,10.7 62.2,14.2 64,19.6 C71.1,19.6 76.5,26.7 74.7,33.8 C76.5,39.1 76.5,46.2 72.9,48 C69.3,55.1 62.2,58.7 53.3,58.7 L53.3,80 Z" fill="#FB8C00"/>
		</svg>
	`
}

/*
Final-action handler. Replay = pop history (fallback profile).
First-launch = fire geoloc prompt (rationale on screen at consent
time), mark onboardingDone, navigate to map. Geo + flag write can each
fail silently without blocking the flow.
*/
async function onFinish(inReplay: boolean): Promise<void>
{
	if (inReplay)
	{
		if (history.length > 1) history.back()
		else navigate(kPaths.profile())
		return
	}

	// Fires the OS GPS prompt now (we're inside the user gesture) AND
	// persists the resolved fix to localStorage so MapScreen can centre
	// on it immediately at its mount that follows the navigate() below.
	// Supersedes the legacy local requestGeolocationPermission() that
	// only requested the prompt without caching the fix.
	bootstrapGeolocation()
	try
	{
		await markOnboardingDone()
	}
	catch (vErr)
	{
		console.warn('Onboarding flag write failed', vErr)
	}
	navigate(kPaths.map())
}
